import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabaseConfig } from "../../lib/supabase";
import { authService } from "../../services/authService";
import { profileService } from "../../services/profileService";
import type { AuthCredentials } from "../../types/auth";
import type { Profile } from "../../types/tenant";

type AuthContextValue = {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  error: string | null;
  isConfigured: boolean;
  clearError: () => void;
  refreshAuth: () => Promise<void>;
  signIn: (credentials: AuthCredentials) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);

function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Something went wrong.";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const hydrateSession = useCallback(async (nextSession: Session | null) => {
    setLoading(true);
    setError(null);

    try {
      if (!nextSession?.user) {
        setSession(null);
        setUser(null);
        setProfile(null);
        return;
      }

      const nextProfile = await profileService.ensureProfile(nextSession.user);
      setSession(nextSession);
      setUser(nextSession.user);
      setProfile(nextProfile);
    } catch (caughtError) {
      setError(getErrorMessage(caughtError));
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshAuth = useCallback(async () => {
    if (!supabaseConfig.isConfigured) {
      setLoading(false);
      return;
    }

    const nextSession = await authService.getCurrentSession();
    await hydrateSession(nextSession);
  }, [hydrateSession]);

  useEffect(() => {
    let isMounted = true;

    async function bootstrap() {
      if (!supabaseConfig.isConfigured) {
        setLoading(false);
        return;
      }

      try {
        const nextSession = await authService.getCurrentSession();

        if (isMounted) {
          await hydrateSession(nextSession);
        }
      } catch (caughtError) {
        if (isMounted) {
          setError(getErrorMessage(caughtError));
          setLoading(false);
        }
      }
    }

    void bootstrap();

    if (!supabaseConfig.isConfigured) {
      return undefined;
    }

    const {
      data: { subscription },
    } = authService.onAuthStateChange((_event, nextSession) => {
      void hydrateSession(nextSession);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [hydrateSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user,
      profile,
      loading,
      error,
      isConfigured: supabaseConfig.isConfigured,
      clearError: () => setError(null),
      refreshAuth,
      signIn: async (credentials) => {
        setError(null);
        const data = await authService.signIn(credentials);
        await hydrateSession(data.session);
      },
      resetPassword: async (email) => {
        setError(null);
        await authService.sendPasswordReset(email);
      },
      signOut: async () => {
        setError(null);
        await authService.signOut();
        setSession(null);
        setUser(null);
        setProfile(null);
      },
    }),
    [
      error,
      hydrateSession,
      loading,
      profile,
      refreshAuth,
      session,
      user,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
